import React, { useState, useEffect } from 'react';
import { Drawer } from '@opendate-app/setlist';
import SalesDetailPage from './SalesDetailPage';
import {
  TOAST_SALES_COLUMNS,
  SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/toastSalesColumnsConfig';
import { compactCurrency, currencyFormatter } from '../utils/reportingUtils';
import { buildToastSalesFilterGroups } from '../constants/toastSalesFiltersConfig';
import ToastFileDeliveryPanel from './ToastFileDeliveryPanel';

const axios = require('axios').default;

const UNMATCHED_BADGE = { background: 'var(--sl-bg-warning-primary, #fffaeb)', color: 'var(--sl-text-warning, #b54708)', fontSize: 12 };

const formatBusinessDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(`${dateStr}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_name && activeFilters.venue_name.length > 0) {
    params.venue_name = activeFilters.venue_name;
  }
  if (activeFilters.match_status && activeFilters.match_status.length > 0) {
    params.match_status = activeFilters.match_status;
  }
  return params;
};

const TOAST_SALES_CONFIG = {
  pageTitle: 'Toast Sales',
  pageSubtitle: 'Every Toast sale by business date, and which show it landed on.',
  sidebarKey: 'food_beverage_toast_sales',
  defaultPreset: 'last_30_days',

  columns: TOAST_SALES_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingToastSalesColumns_${teamId}`,
  firstColumnId: 'business_date',

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/food-and-beverage/toast-sales/data.json`,
  detailResponseKey: 'toast_sales_rows',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/food-and-beverage/toast-sales/export`,
  salesExportSection: 'toast_sales',
  detailErrorMessage: 'Failed to load Toast sales.',
  detailTableTitle: 'Business Days',
  emptyMessage: 'No Toast sales received in this range.',
  searchPlaceholder: 'Search item, category, show...',
  paginationLabel: 'days',
  itemLabel: (count) => `day${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  buildKpiCards: (current, prior) => [
    {
      label: 'Toast Net Sales',
      value: compactCurrency(current.net_sales || 0),
      current: current.net_sales || 0,
      prior: prior.net_sales || 0,
    },
    {
      label: 'Matched to Shows',
      value: compactCurrency(current.matched_net_sales || 0),
      current: current.matched_net_sales || 0,
      prior: prior.matched_net_sales || 0,
    },
    {
      label: 'Unmatched',
      value: compactCurrency(current.unmatched_net_sales || 0),
      current: current.unmatched_net_sales || 0,
      prior: prior.unmatched_net_sales || 0,
    },
    {
      label: 'Days With Unmatched Sales',
      value: (current.days_with_unmatched || 0).toLocaleString(),
      current: current.days_with_unmatched || 0,
      prior: prior.days_with_unmatched || 0,
    },
  ],
  kpiGridColumns: 4,

  chartColor: 'var(--sl-fg-brand, #1982C4)',
  overTimeKey: null,

  filterTitle: 'Filter Toast Sales',
  filterSubtitle: 'Narrow by venue or whether every sale found a show.',
};

const ShowLink = ({ teamId, destination, formatEventDate }) => {
  if (!destination.calendar_event_id) {
    return <span className="badge" style={UNMATCHED_BADGE}>No show</span>;
  }

  return (
    <a href={`/teams/${teamId}/confirms/${destination.calendar_event_id}/profit_and_loss`}>
      {destination.event_name || `Show #${destination.calendar_event_id}`}
      {destination.event_date && (
        <span className="text-muted small ml-1">({formatEventDate(destination.event_date)})</span>
      )}
    </a>
  );
};

const CategoryBreakdown = ({ category, teamId, formatEventDate, formatEventDateTime }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border rounded mb-3">
      <button
        type="button"
        className="btn btn-link btn-block text-left d-flex justify-content-between align-items-center px-3 py-2 text-body"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span>
          <strong>{category.sales_category}</strong>
          <span className="text-muted small ml-2">{category.line_count.toLocaleString()} lines</span>
        </span>
        <strong>{currencyFormatter.format(category.net_sales)}</strong>
      </button>

      <ul className="list-unstyled mb-0 px-3 pb-2 small">
        {category.destinations.map(destination => (
          <li key={destination.calendar_event_id || 'unmatched'} className="d-flex justify-content-between py-1">
            <ShowLink teamId={teamId} destination={destination} formatEventDate={formatEventDate} />
            <span>{currencyFormatter.format(destination.net_sales)}</span>
          </li>
        ))}
      </ul>

      {expanded && (
        <div className="table-responsive border-top">
          <table className="table table-sm mb-0 small">
            <thead>
              <tr>
                <th>Time</th>
                <th>Item</th>
                <th>Check</th>
                <th>Server</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Net</th>
                <th>Show</th>
              </tr>
            </thead>
            <tbody>
              {category.lines.map(line => (
                <tr key={line.id}>
                  <td className="text-nowrap">{formatEventDateTime(line.order_at)}</td>
                  <td>
                    {line.menu_item || '—'}
                    {line.menu_group && <div className="text-muted">{line.menu_group}</div>}
                  </td>
                  <td>{line.tab_name || line.check_id || '—'}</td>
                  <td>{line.server || '—'}</td>
                  <td className="text-right">{line.quantity}</td>
                  <td className="text-right">{currencyFormatter.format(line.net_price)}</td>
                  <td>
                    <ShowLink teamId={teamId} destination={line} formatEventDate={formatEventDate} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const BreakdownDrawer = ({ teamId, day, onClose, formatEventDate, formatEventDateTime }) => {
  const [breakdown, setBreakdown] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!day) return;

    setBreakdown(null);
    setError(null);

    axios.get(`/teams/${teamId}/reporting/food-and-beverage/toast-sales/breakdown.json`, {
      params: { venue_ownership_id: day.venue_ownership_id, business_date: day.business_date_path },
    })
      .then((res) => setBreakdown(res.data))
      .catch((err) => {
        console.error('Failed to load Toast sales breakdown', err);
        setError('Failed to load this day’s Toast sales.');
      });
  }, [teamId, day?.id]);

  return (
    <Drawer
      open={!!day}
      onClose={onClose}
      size="xl"
      title={day ? `${day.venue_name} — ${formatBusinessDate(day.business_date)}` : ''}
      subtitle="Toast sales for this business date, by sales category, and the show each sale landed on."
    >
      {error && <div className="alert alert-danger">{error}</div>}
      {!error && !breakdown && <div className="text-muted">Loading…</div>}

      {breakdown && (
        <>
          <div className="d-flex mb-3">
            <div className="mr-4">
              <div className="text-muted small">Toast net sales</div>
              <strong>{currencyFormatter.format(breakdown.net_sales)}</strong>
            </div>
            <div>
              <div className="text-muted small">Unmatched</div>
              <strong className={breakdown.unmatched_net_sales > 0 ? 'text-warning' : ''}>
                {currencyFormatter.format(breakdown.unmatched_net_sales)}
              </strong>
            </div>
          </div>

          <h6 className="mb-2">Where it went</h6>
          <ul className="list-unstyled mb-4 small">
            {breakdown.destinations.map(destination => (
              <li key={destination.calendar_event_id || 'unmatched'} className="d-flex justify-content-between py-1 border-bottom">
                <ShowLink teamId={teamId} destination={destination} formatEventDate={formatEventDate} />
                <span>
                  {currencyFormatter.format(destination.net_sales)}
                  <span className="text-muted ml-2">{destination.line_count.toLocaleString()} lines</span>
                </span>
              </li>
            ))}
          </ul>

          <h6 className="mb-2">By sales category</h6>
          {breakdown.categories.length === 0 && <div className="text-muted small">No sales on this business date.</div>}
          {breakdown.categories.map(category => (
            <CategoryBreakdown
              key={category.sales_category}
              category={category}
              teamId={teamId}
              formatEventDate={formatEventDate}
              formatEventDateTime={formatEventDateTime}
            />
          ))}

          {breakdown.void_lines.length > 0 && (
            <div className="text-muted small mt-3">
              {breakdown.void_lines.length.toLocaleString()} voided lines excluded.
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

function ReportingFoodAndBeverageToastSalesPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [formatters, setFormatters] = useState(null);

  useEffect(() => {
    if (!team?.id) return;
    axios.get(`/teams/${team.id}/reporting/food-and-beverage/toast-sales/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch((error) => console.error('Failed to load Toast sales filter options', error));
  }, [team?.id]);

  const configWithHandlers = {
    ...TOAST_SALES_CONFIG,
    buildFilterGroups: () => buildToastSalesFilterGroups(filterOptions),
    renderNotice: () => <ToastFileDeliveryPanel team={team} csrfToken={csrfToken} />,
    renderCellValue: (col, row, rowFormatters) => {
      const { formatEventDate, currencyFormatter: cf } = rowFormatters;

      switch (col.id) {
        case 'business_date':
          return <span className="text-nowrap">{formatBusinessDate(row.business_date)}</span>;
        case 'venue_name':
          return row.venue_name || '—';
        case 'line_count':
          return (row.line_count || 0).toLocaleString();
        case 'net_sales':
          return cf.format(row.net_sales || 0);
        case 'matched_net_sales':
          return cf.format(row.matched_net_sales || 0);
        case 'unmatched_net_sales':
          if (!row.unmatched_net_sales && !row.unmatched_line_count) return cf.format(0);
          return <span className="badge" style={UNMATCHED_BADGE}>{cf.format(row.unmatched_net_sales || 0)}</span>;
        case 'events':
          if (!row.events || row.events.length === 0) return <span className="text-muted">—</span>;
          return (
            <ul className="list-unstyled mb-0 small">
              {row.events.map(event => (
                <li key={event.calendar_event_id} className="text-nowrap">
                  <ShowLink teamId={team.id} destination={event} formatEventDate={formatEventDate} />
                  <span className="text-muted ml-1">{cf.format(event.net_sales)}</span>
                </li>
              ))}
            </ul>
          );
        case 'void_total':
          return cf.format(row.void_total || 0);
        case 'actions':
          return (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary text-nowrap"
              onClick={() => {
                setFormatters(rowFormatters);
                setSelectedDay(row);
              }}
            >
              View breakdown
            </button>
          );
        default:
          return '—';
      }
    },
  };

  return (
    <>
      <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithHandlers} />
      {formatters && (
        <BreakdownDrawer
          teamId={team.id}
          day={selectedDay}
          onClose={() => setSelectedDay(null)}
          formatEventDate={formatters.formatEventDate}
          formatEventDateTime={formatters.formatEventDateTime}
        />
      )}
    </>
  );
}

export default ReportingFoodAndBeverageToastSalesPage;
